import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { DataSourceOrdenacaoInsensivel } from '../../../../shared/helpers/ordenacao-tabela.helper';
import { AlertCardComponent } from '../../../../shared/components/alert-card/alert-card.component';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { UtilService } from '../../../../shared/services/util.service';
import { CondicaoPagamentoService } from '../../../../shared/services/apis/financas/condicao-pagamento.service';
import { CondicaoPagamento } from '../../../../shared/services/models/financas/condicao-pagamento.model';
import { ConfigVendasGeralService } from '../../../../shared/services/apis/vendas/config-vendas-geral.service';
import { CondicaoPagamentoModoEnum } from '../../../../shared/enums/financas/condicao-pagamento-modo.enum';
import { GetConfigVendasGeralResponse } from '@shared/services/responses/vendas/config-vendas-geral.response';

@Component({
    selector: 'app-condicoes-pagamento-list',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatTableModule,
        MatCheckboxModule,
        MatMenuModule,
        MatTooltipModule,
        MatSortModule,
        MatProgressSpinnerModule,
        MatDialogModule,
        EmptyListComponent,
        AlertCardComponent,
        RouterModule,
        HeaderComponent,
        LoadingBoxDefaultListComponent
    ],
    templateUrl: './condicoes-pagamento-list.component.html',
    styleUrl: './condicoes-pagamento-list.component.css'
})
export class CondicoesPagamentoListComponent implements OnInit, OnDestroy {
    public moduloEnum = ModuloEnum.Configuracoes;
    public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Financas;

    public model: FormGroup;
    public _paginaAtual: number = 1;
    public _tamanhoPagina: number = 20;
    public _totalRegistros: number = 0;
    public _dataSource = new DataSourceOrdenacaoInsensivel<CondicaoPagamento>();
    public _isLoading: boolean = false;

    public displayedColumns: string[] = [
        'select',
        'condicao',
        'estrutura',
        'juros',
        'formasAplicaveis',
        'situacao',
        'actions'
    ];

    public selection = new SelectionModel<CondicaoPagamento>(true, []);

    public isCondicaoPagamentoLivre: boolean = true;

    @ViewChild(MatSort) set matSort(sort: MatSort) {
        this._dataSource.sort = sort;
    }

    constructor(
        private formBuilder: FormBuilder,
        private alertService: AlertService,
        private dialog: MatDialog,
        private utilService: UtilService,
        private condicaoPagamentoService: CondicaoPagamentoService,
        private configVendasGeralService: ConfigVendasGeralService
    ) {
        this._dataSource.sortingDataAccessor = (item, property) => {
            const value = (item as any)[property];
            return typeof value === 'string' ? value.toLowerCase() : value;
        };
    }

    ngOnInit(): void {
        this.model = this.formBuilder.group({
            pesquisa: ['']
        });

        this.carregarConfigVendasGeral();
        this.loadFilters();
        this.carregarDados(true);
    }

    /**
     * Chama o endpoint real de Configurações >> Vendas >> Geral (via /lookup, sem gate de permissão
     * de Vendas — leitura cross-módulo).
     */
    private carregarConfigVendasGeral(): void {
        this.configVendasGeralService.getLookup().subscribe({
            next: (res: GetConfigVendasGeralResponse) => {
                if (res.isError || !res.configVendasGeral) return;
                this.isCondicaoPagamentoLivre = res.configVendasGeral.isCondicaoPagamentoLivre;
            }
        });
    }

    ngOnDestroy(): void {
        this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
            '/configuracoes/financas/condicoes-pagamento/cadastrar',
            '/configuracoes/financas/condicoes-pagamento/cadastrar/'
        ]);
    }

    private loadFilters(): void {
        const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
        if (filtroSalvo) {
            this.model.patchValue(filtroSalvo);
        }
    }

    public carregarDados(reset: boolean = false): void {
        this._isLoading = true;

        if (reset) {
            this._paginaAtual = 1;
            this.selection.clear();
            this._dataSource.data = [];
        }

        const pesquisa = this.model?.get('pesquisa')?.value || '';

        this.condicaoPagamentoService.get(this._paginaAtual, this._tamanhoPagina, pesquisa).subscribe({
            next: (res) => {
                this._isLoading = false;
                if (res.isError) {
                    this.alertService.show('Atenção', res.errorDescription || 'Erro ao carregar condições de pagamento.', AlertType.warning);
                    return;
                }

                this._totalRegistros = res.totalRegistros;
                const items = res.listaCondicaoPagamento || [];

                if (this._paginaAtual === 1) {
                    this._dataSource.data = items;
                } else {
                    this._dataSource.data = [...this._dataSource.data, ...items];
                }
            },
            error: () => {
                this._isLoading = false;
                this.alertService.show('Erro', 'Erro ao carregar condições de pagamento.', AlertType.error);
            }
        });
    }

    public proximaPagina(): void {
        this._paginaAtual++;
        this.carregarDados();
    }

    public clickPesquisar(): void {
        this.carregarDados(true);
    }

    public limparFiltros(): void {
        this.model.patchValue({ pesquisa: '' });
        this.clickPesquisar();
    }

    public isAllSelected(): boolean {
        return this._dataSource.data.length > 0 && this.selection.selected.length === this._dataSource.data.length;
    }

    public toggleAllRows(): void {
        if (this.isAllSelected()) {
            this.selection.clear();
            return;
        }
        this.selection.select(...this._dataSource.data);
    }

    /** Dias de vencimento de cada parcela — mesmo cálculo do cadastro (Story, seção 2.2). */
    public diasVencimento(item: CondicaoPagamento): number[] {
        if (item.idCondicaoPagamentoModo !== CondicaoPagamentoModoEnum.IntervaloUnico) {
            return (item.diasPorParcela || [])
                .slice()
                .sort((a, b) => a.ordem - b.ordem)
                .map(p => p.dias);
        }

        const dias: number[] = [];
        for (let i = 0; i < item.numeroParcelas; i++) {
            dias.push((i + 1) * item.intervaloDias);
        }
        return dias;
    }

    public resumoVencimentos(item: CondicaoPagamento): string {
        return this.diasVencimento(item).map(d => d === 0 ? 'à vista' : `${d}d`).join(' · ');
    }

    public parcelasLabel(item: CondicaoPagamento): string {
        return item.numeroParcelas === 1 ? '1 parcela' : `${item.numeroParcelas} parcelas`;
    }

    public modoLabel(item: CondicaoPagamento): string {
        return item.idCondicaoPagamentoModo === CondicaoPagamentoModoEnum.IntervaloUnico
            ? `a cada ${item.intervaloDias} dias`
            : 'dias por parcela';
    }

    public getStatusClass(isAtivo: boolean): string {
        return isAtivo ? 'badge-verde' : 'badge-cinza';
    }

    public getTooltipAcoesSemSelecao(): string {
        return this.selection.hasValue() ? '' : 'Selecione ao menos uma condição na lista para excluir em lote.';
    }

    public excluir(item: CondicaoPagamento): void {
        if (item.emUso) {
            this.alertService.show('Exclusão bloqueada',
                `${item.nome} já foi usada em documentos. Desative a condição em vez de excluí-la.`,
                AlertType.error);
            return;
        }

        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Excluir condição',
                description: `A condição ${item.nome} será excluída. Documentos futuros deixam de oferecê-la.`,
                buttonConfirm: 'Excluir',
                buttonClose: 'Cancelar'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result?.confirm) return;

            this._isLoading = true;
            this.condicaoPagamentoService.bulkDelete([item.idCondicaoPagamento]).subscribe({
                next: (res) => {
                    this._isLoading = false;
                    if (res.isError) {
                        this.alertService.show('Atenção', res.errorDescription || 'Erro ao excluir condição de pagamento.', AlertType.warning);
                        return;
                    }
                    this.selection.deselect(item);
                    this.alertService.show('Condição excluída', `${item.nome} foi removida do cadastro.`, AlertType.success);
                    this.carregarDados(true);
                },
                error: () => {
                    this._isLoading = false;
                    this.alertService.show('Erro', 'Erro ao excluir condição de pagamento.', AlertType.error);
                }
            });
        });
    }

    public excluirSelecionados(): void {
        const selecionadas = this.selection.selected;
        if (selecionadas.length === 0) return;

        const emUso = selecionadas.filter(c => c.emUso);
        if (emUso.length > 0) {
            this.alertService.show('Exclusão bloqueada',
                emUso.length === 1
                    ? `"${emUso[0].nome}" já foi usada em documentos. Desative-a em vez de excluir.`
                    : `${emUso.length} das condições selecionadas já foram usadas em documentos. Desative-as em vez de excluir.`,
                AlertType.error);
            return;
        }

        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Excluir condições',
                description: selecionadas.length === 1
                    ? `A condição "${selecionadas[0].nome}" será excluída. Documentos futuros deixam de oferecê-la.`
                    : `${selecionadas.length} condições serão excluídas: ${selecionadas.map(c => c.nome).join(', ')}. Documentos futuros deixam de oferecê-las.`,
                buttonConfirm: 'Excluir',
                buttonClose: 'Cancelar'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result?.confirm) return;

            this._isLoading = true;
            this.condicaoPagamentoService.bulkDelete(selecionadas.map(c => c.idCondicaoPagamento)).subscribe({
                next: (res) => {
                    this._isLoading = false;
                    if (res.isError) {
                        this.alertService.show('Atenção', res.errorDescription || 'Erro ao excluir condições de pagamento.', AlertType.warning);
                        return;
                    }
                    this.selection.clear();
                    this.alertService.show('Condições excluídas',
                        `${selecionadas.length} ${selecionadas.length === 1 ? 'condição foi removida' : 'condições foram removidas'} do cadastro.`,
                        AlertType.success);
                    this.carregarDados(true);
                },
                error: () => {
                    this._isLoading = false;
                    this.alertService.show('Erro', 'Erro ao excluir condições de pagamento.', AlertType.error);
                }
            });
        });
    }
}
